'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useTranslations } from "next-intl";
import { IoMdCloseCircle } from "react-icons/io";
import { useLocale } from 'next-intl';
import { FaCircleNotch, FaStar, FaCamera, FaTimes } from "react-icons/fa";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";

const COUNTRIES = [
  { code: "DZ", name: "Algeria", flag: "🇩🇿" },
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "SA", name: "Saudi Arabia", flag: "🇸🇦" },
  { code: "AE", name: "UAE", flag: "🇦🇪" },
  { code: "EG", name: "Egypt", flag: "🇪🇬" },
  { code: "MA", name: "Morocco", flag: "🇲🇦" },
  { code: "TN", name: "Tunisia", flag: "🇹🇳" },
  { code: "TR", name: "Turkey", flag: "🇹🇷" },
  { code: "IT", name: "Italy", flag: "🇮🇹" },
  { code: "ES", name: "Spain", flag: "🇪🇸" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "JP", name: "Japan", flag: "🇯🇵" },
  { code: "CN", name: "China", flag: "🇨🇳" },
  { code: "IN", name: "India", flag: "🇮🇳" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "MX", name: "Mexico", flag: "🇲🇽" },
  { code: "RU", name: "Russia", flag: "🇷🇺" },
  { code: "KW", name: "Kuwait", flag: "🇰🇼" },
  { code: "QA", name: "Qatar", flag: "🇶🇦" },
  { code: "LY", name: "Libya", flag: "🇱🇾" },
  { code: "SD", name: "Sudan", flag: "🇸🇩" },
  { code: "IQ", name: "Iraq", flag: "🇮🇶" },
  { code: "JO", name: "Jordan", flag: "🇯🇴" },
  { code: "LB", name: "Lebanon", flag: "🇱🇧" },
  { code: "SY", name: "Syria", flag: "🇸🇾" },
  { code: "YE", name: "Yemen", flag: "🇾🇪" },
];

export default function AddReview({
  userid,
  productid,
  color,
  size,
}: {
  userid: any;
  productid: number;
  color: any;
  size: any;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [review, setReview] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const dialogRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const t = useTranslations('orders');
  const l = useLocale();
  const te = useTranslations('Login');
  const { data: session } = useSession({ required: true });
  const router = useRouter();

  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const selectedCountry = COUNTRIES.find((c) => c.code === country);

  // Handle image file selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Max 5 images total
    const combined = [...images, ...files].slice(0, 5);
    setImages(combined);

    // Generate preview URLs
    const previews = combined.map((file) => URL.createObjectURL(file));
    setImagePreviews(previews);

    // Reset input so same file can be re-selected after removal
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Remove a specific image by index
  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImages(newImages);
    setImagePreviews(newPreviews);
  };

  const resetForm = () => {
    setReview('');
    setRating(0);
    setHoverRating(0);
    setName('');
    setCountry('');
    setImages([]);
    setImagePreviews([]);
    setError('');
  };

  const handleSubscribe = async () => {
    if (rating === 0) {
      setError(t('write-rating-error'));
      return;
    }
    if (!review) {
      setError(t('write-review-error'));
      return;
    }
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!country) {
      setError('Please select your country.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Step 1: POST the review and get back the new review ID
      const orderItem = {
        user: userid,
        product: productid,
        rating,
        comment: review,
        name,
        country,
        created_at: today,
        updated_at: today,
        color,
        size,
      };

      const reviewResponse = await fetch(`${process.env.NEXT_PUBLIC_URL}productreviews/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Token " + process.env.NEXT_PUBLIC_TOKEN,
        },
        body: JSON.stringify(orderItem),
      });

      const rawReview = await reviewResponse.text();

      if (!reviewResponse.ok) {
        const errorData = JSON.parse(rawReview);
        setError(errorData.message || te('Subscription-failed'));
        return;
      }

      const createdReview = JSON.parse(rawReview);
      const reviewId = createdReview.id;

      // Step 2: Upload each image separately to reviewsimage/
      if (images.length > 0 && reviewId) {
        const imageUploads = images.map((file) => {
          const formData = new FormData();
          formData.append('ProductReview', String(reviewId));
          formData.append('image', file);

          return fetch(`${process.env.NEXT_PUBLIC_URL}reviewsimage/`, {
            method: "POST",
            headers: {
              // Do NOT set Content-Type manually — browser sets it with the correct multipart boundary
              "Authorization": "Token " + process.env.NEXT_PUBLIC_TOKEN,
            },
            body: formData,
          });
        });

        await Promise.all(imageUploads);
      }

      // Close and reset on success
      setIsOpen(false);
      resetForm();
    } catch (err) {
      console.error("Error submitting review:", err);
      setError(te('Subscription-failed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Close the dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      {/* Trigger Button */}
      <button
        className="border border-spacing-1 rounded-xl bg-black font-extrabold py-1 px-2 text-sm capitalize hover:bg-gray-700 text-yel"
        onClick={() => setIsOpen(true)}
      >
        + Review
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            ref={dialogRef}
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full relative max-h-[90vh] overflow-y-auto"
          >
            {/* Close Button */}
            <IoMdCloseCircle
              size={24}
              className={`absolute top-2 ${l === "ar" ? "left-2" : "right-2"} text-gray-500 hover:text-gray-700 cursor-pointer`}
              onClick={() => setIsOpen(false)}
            />

            <h2 className="text-xl font-semibold text-gray-800 mb-4 uppercase">
              {t('Write-a-Review')}
            </h2>
            <p className="text-gray-600 mb-6 text-sm">{t('description-leave-feedback')}</p>

            <div className="flex flex-col space-y-4">

              {/* Star Rating */}
              <div className="flex items-center">
                <p className="text-gray-600 mr-3 text-sm">{t('Rating')}:</p>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={`cursor-pointer text-2xl ${
                        star <= (hoverRating || rating) ? 'text-yellow-500' : 'text-gray-300'
                      }`}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(star)}
                    />
                  ))}
                </div>
              </div>

              {/* Name Field */}
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yel text-sm"
              />

              {/* Country Select with Flag */}
              <div className="flex items-center border border-gray-300 rounded-lg px-4 py-2 focus-within:ring-1 focus-within:ring-yel">
                {selectedCountry && (
                  <span className="text-xl mr-2 leading-none">{selectedCountry.flag}</span>
                )}
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full focus:outline-none text-sm text-gray-700 bg-transparent appearance-none cursor-pointer"
                >
                  <option value="">Select your country</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Review Textarea */}
              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder={t('write-review-placeholder')}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yel text-sm"
              />

              {/* ── Image Upload Section ── */}
              <div>
                <p className="text-gray-600 text-sm mb-2">
                  Photos{' '}
                  <span className="text-gray-400 text-xs">(optional · up to 5)</span>
                </p>

                {/* Previews grid */}
                {imagePreviews.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {imagePreviews.map((src, index) => (
                      <div
                        key={index}
                        className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 group"
                      >
                        <img
                          src={src}
                          alt={`preview-${index}`}
                          className="w-full h-full object-cover"
                        />
                        {/* Hover overlay with remove icon */}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <FaTimes className="text-white text-sm" />
                        </button>
                      </div>
                    ))}

                    {/* "+ add more" tile */}
                    {images.length < 5 && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors"
                      >
                        <FaCamera className="text-lg" />
                      </button>
                    )}
                  </div>
                )}

                {/* Empty state — large upload area */}
                {imagePreviews.length === 0 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg py-5 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FaCamera className="text-2xl" />
                    <span className="text-xs">Click to upload photos</span>
                  </button>
                )}

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageChange}
                />
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              {/* Submit Button */}
              <button
                onClick={handleSubscribe}
                disabled={isLoading}
                className={`w-full py-2 rounded-lg font-medium transition-colors uppercase flex gap-3 justify-center items-center ${
                  isLoading
                    ? 'bg-yel text-black cursor-not-allowed'
                    : 'bg-secondary text-yel hover:bg-yel hover:text-black'
                }`}
              >
                {t('Submit-review')}
                {isLoading && <FaCircleNotch className="animate-spin w-5 h-5" />}
              </button>

              <p className="text-gray-500 text-xs">{t('small-description')}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}